import React, { FC, ReactNode } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { Button, Checkbox } from '@clearcover/atlas';
import { Asset } from '../Asset';
import { Loading } from '../Loading/Loading';
import styles from './styles.module.css';

export type SortHeader = {
  title: string;
  onClick: () => void;
  sortDirection: 'asc' | 'desc' | 'none';
};

const isSortHeader = (value: any): value is SortHeader => {
  return (
    value &&
    typeof value === 'object' &&
    'title' in value &&
    'onClick' in value &&
    'sortDirection' in value
  );
};

type CellObject = {
  component: ReactNode;
  allowOverflow?: boolean;
};

const isCellObject = (value: ReactNode | CellObject): value is CellObject => {
  return Boolean(
    typeof value === 'object' && value?.hasOwnProperty('component')
  );
};

export type TableProps = {
  rows: Array<{
    key: string | number;
    data: Array<ReactNode | CellObject>;
    className?: string;
    onViewRow?(): void;
    onClick?(): void;
    viewRowText?: ReactNode;
    additionalContent?: ReactNode;
    checkboxSelected?: boolean;
  }>;
  headers?: Array<SortHeader | ReactNode>;
  headerAlignment?: 'center' | 'left';
  headerClassName?: string;
  footerEl?: ReactNode;
  name?: string;
  loading?: boolean;
  className?: string;
  columnWidths?: Array<string | number>;
  columnClassNames?: Array<string> | string;
  columnDataTestIdPrefix?: Array<string | null>;
  checkboxSelection?: boolean;
  onCheckboxSelect?: (key: string | number, selected: boolean) => void;
  onCheckboxSelectAll?: (selected: boolean) => void;
  hasPagination?: boolean;
  striped?: boolean;
};

export const Table: FC<TableProps> = ({
  rows,
  headers,
  headerAlignment = 'left',
  headerClassName,
  footerEl,
  name = 'table',
  loading = false,
  className,
  columnWidths = [],
  columnClassNames = [],
  columnDataTestIdPrefix = [],
  checkboxSelection = false,
  onCheckboxSelect = () => {},
  onCheckboxSelectAll = () => {},
  hasPagination = false,
  striped = true,
}) => {
  const columnFlexStyles: Array<React.CSSProperties> = columnWidths.map(value =>
    typeof value === 'string'
      ? {
          flexGrow: 0,
          flexBasis: value,
        }
      : {
          flexGrow: value,
          flexShrink: value,
        }
  );

  const hasViewColumn = rows.some(row => !!row.onViewRow);
  const selectedCheckboxes = rows.filter(row => row.checkboxSelected);

  return (
    <div
      className={classNames(
        'fs-mask',
        styles.table,
        { [styles.hasPagination]: hasPagination, [styles.striped]: striped },
        className
      )}
      data-testid={name}
    >
      {headers && (
        <div
          className={classNames(styles.header, headerClassName)}
          data-testid={`${name}-header`}
        >
          {checkboxSelection && (
            <div
              className={classNames(
                styles.headerCell,
                styles.checkboxContainer
              )}
              style={{
                textAlign: headerAlignment,
              }}
            >
              <Checkbox
                className={styles.checkbox}
                name="selectAllCheckbox"
                label="Select All"
                value={
                  rows.length > 0 && selectedCheckboxes.length === rows.length
                }
                indeterminate={
                  selectedCheckboxes.length > 0 &&
                  selectedCheckboxes.length !== rows.length
                }
                hideLabel
                onChange={value => {
                  onCheckboxSelectAll(value);
                }}
              />
            </div>
          )}
          {headers.map((header, index) =>
            isSortHeader(header) ? (
              <div
                key={header.title}
                className={classNames(styles.headerCell, styles.sortable)}
                style={{
                  textAlign: headerAlignment,
                  ...columnFlexStyles[index],
                }}
                onClick={header.onClick}
                data-testid={`sort-${header.title
                  .toLowerCase()
                  .replace(/\W/g, '-')}`}
              >
                <div className={styles.item}>{header.title}</div>
                {header.sortDirection !== 'none' && (
                  <Asset
                    variant={
                      header.sortDirection === 'asc'
                        ? 'dropdownDown'
                        : 'dropdownUp'
                    }
                  />
                )}
              </div>
            ) : (
              <div
                key={index}
                className={styles.headerCell}
                style={{
                  textAlign: headerAlignment,
                  ...columnFlexStyles[index],
                }}
              >
                <div className={styles.item}>{header}</div>
              </div>
            )
          )}
          {hasViewColumn && (
            <div
              key="view-header"
              className={classNames(styles.headerCell, styles.viewHeader)}
            >
              View
            </div>
          )}
        </div>
      )}
      {loading ? (
        <div className={styles.loading} data-testid="table-loader">
          <Loading />
        </div>
      ) : (
        <>
          <ul data-testid={`${name}-body`}>
            {rows.map((row, index) => (
              <li
                key={`${name}-${index}`}
                onClick={row.onClick}
                className={classNames(styles.row, row.className)}
                data-testid={`row-${index}`}
              >
                {checkboxSelection && (
                  <div
                    className={classNames(
                      styles.cell,
                      styles.checkboxContainer
                    )}
                  >
                    <Checkbox
                      className={styles.checkbox}
                      name={row.key.toString()}
                      label={row.key.toString()}
                      value={Boolean(row.checkboxSelected)}
                      hideLabel
                      onChange={value => {
                        onCheckboxSelect(row.key, value);
                      }}
                    />
                  </div>
                )}
                {row.data.map((data, colIndex) => (
                  <div
                    data-testid={`${columnDataTestIdPrefix[colIndex] ??
                      'col'}-${colIndex}`}
                    key={colIndex}
                    className={classNames(
                      styles.cell,
                      typeof columnClassNames === 'string'
                        ? columnClassNames
                        : columnClassNames[colIndex]
                    )}
                    style={{
                      ...columnFlexStyles[colIndex],
                    }}
                  >
                    {isCellObject(data) && data.allowOverflow ? (
                      data.component
                    ) : (
                      <div className={styles.item}>
                        {isCellObject(data) ? data.component : data}
                      </div>
                    )}
                  </div>
                ))}
                {hasViewColumn && (
                  <Button
                    link
                    key={`view-link-${index}`}
                    data-testid={`view-link-${index}`}
                    className={classNames(styles.cell, styles.viewLink, {
                      [styles.hidden]: !row.onViewRow,
                    })}
                    onClick={row.onViewRow}
                  >
                    {row.viewRowText ? row.viewRowText : 'View'}
                  </Button>
                )}
                {row.additionalContent && (
                  <div className={styles.additionalContent}>
                    {row.additionalContent}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {footerEl}
        </>
      )}
    </div>
  );
};

export const EmptyTable: FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.7 }}
    animate={{ opacity: 1, scale: 1 }}
    className={styles.emptyContainer}
  >
    {children}
  </motion.div>
);

export const SimpleTable: FC<TableProps & { children?: React.ReactNode }> = ({
  children,
  ...props
}) => (
  <div className={styles.simpleTable}>
    <Table {...props}>{children}</Table>
  </div>
);

export const EmptyFilterTable: FC<{
  id?: string;
  children?: React.ReactNode;
}> = ({ children, id }) => (
  <EmptyTable>
    <div className={styles.emptyFilterContainer} data-testid={id}>
      {children}
    </div>
  </EmptyTable>
);
